/**
 * CatAnimator — drives a cat's skeletal clips from its movement state.
 *
 * The clips come from tools/blender/rig_cat.py (idle, walk, run, jump,
 * fall, land; animated in place). Two layers:
 *
 *  - `stepCatPose` is pure: movement state in, target clip weights and
 *    playback speeds out. Unit-tested without Babylon.
 *  - `CatAnimator` owns the AnimationGroups and eases their weights toward
 *    those targets each render frame.
 *
 * Walk/run playback speed is locked to ground speed using each clip's
 * stride (distance one cycle covers), so paws don't skate.
 */
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";

export const CAT_CLIPS = ["idle", "walk", "run", "jump", "fall", "land"] as const;
export type CatClip = (typeof CAT_CLIPS)[number];

export interface CatClipInfo {
  /** Clip length in seconds at speedRatio 1. */
  duration: number;
  /** Ground distance one cycle covers (walk/run only), in model units × modelScale. */
  stride?: number;
}

export interface CatMotion {
  grounded: boolean;
  /** Horizontal speed, world units/s (sign ignored). */
  speed: number;
  /** Vertical velocity, world units/s. */
  vy: number;
  jumped: boolean;
  landed: boolean;
}

export interface CatPoseState {
  /** One-shot currently playing, and seconds into it. */
  oneShot: "jump" | "land" | null;
  oneShotTime: number;
}

export interface CatPoseTargets {
  weights: Record<CatClip, number>;
  speeds: Record<CatClip, number>;
  /** One-shot that must restart from frame 0 this frame. */
  restart: "jump" | "land" | null;
}

/** Below this the cat is standing; walk fully in by WALK_FULL. */
export const IDLE_SPEED = 0.25;
export const WALK_FULL = 1.5;
/**
 * Default walk → run cross-fade band (world units/s; cats top out at 5).
 * With stride data the band follows the clips instead: run takes over
 * once walk would play faster than WALK_MAX_RATE, and is fully in by the
 * speed where run plays at RUN_FULL_RATE.
 */
export const RUN_START = 3.2;
export const RUN_FULL = 4.6;
export const WALK_MAX_RATE = 1.6;
export const RUN_FULL_RATE = 0.8;

export function locomotionBand(clips: Record<CatClip, CatClipInfo>): { walkFull: number; runStart: number; runFull: number } {
  const natural = (c: CatClipInfo) => (c.stride && c.duration > 0 ? c.stride / c.duration : 0);
  const walkNat = natural(clips.walk);
  const runNat = natural(clips.run);
  if (!walkNat || !runNat) return { walkFull: WALK_FULL, runStart: RUN_START, runFull: RUN_FULL };
  const runStart = Math.max(IDLE_SPEED + 0.2, walkNat * WALK_MAX_RATE);
  const runFull = Math.max(runStart + 0.5, runNat * RUN_FULL_RATE);
  return { walkFull: Math.min(WALK_FULL, runStart), runStart, runFull };
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export function initialCatPoseState(): CatPoseState {
  return { oneShot: null, oneShotTime: 0 };
}

/**
 * Advance the pose state by `dt` and return the target mix. Mutates
 * `state` (one-shot bookkeeping) — cheap and allocation-light because it
 * runs per render frame for two cats.
 */
export function stepCatPose(
  state: CatPoseState,
  motion: CatMotion,
  clips: Record<CatClip, CatClipInfo>,
  dt: number,
): CatPoseTargets {
  let restart: CatPoseTargets["restart"] = null;
  if (motion.jumped) {
    state.oneShot = "jump";
    state.oneShotTime = 0;
    restart = "jump";
  } else if (motion.landed) {
    state.oneShot = "land";
    state.oneShotTime = 0;
    restart = "land";
  } else if (state.oneShot) {
    state.oneShotTime += dt;
    const done = state.oneShotTime >= clips[state.oneShot].duration;
    // A landing interrupted by walking off a ledge ends early; a jump
    // ends early if the cat is already back on the ground.
    const interrupted =
      (state.oneShot === "land" && !motion.grounded) ||
      (state.oneShot === "jump" && motion.grounded);
    if (done || interrupted) state.oneShot = null;
  }

  const weights: Record<CatClip, number> = { idle: 0, walk: 0, run: 0, jump: 0, fall: 0, land: 0 };
  const speeds: Record<CatClip, number> = { idle: 1, walk: 1, run: 1, jump: 1, fall: 1, land: 1 };

  if (state.oneShot === "jump" || (!motion.grounded && state.oneShot !== "land")) {
    if (state.oneShot === "jump") weights.jump = 1;
    else weights.fall = 1;
  } else {
    const band = locomotionBand(clips);
    const move = clamp01((motion.speed - IDLE_SPEED) / (band.walkFull - IDLE_SPEED));
    const run = clamp01((motion.speed - band.runStart) / (band.runFull - band.runStart));
    let idle = 1 - move;
    let walk = move * (1 - run);
    let runW = move * run;
    if (state.oneShot === "land") {
      // Landing owns the pose early on, then hands back to locomotion.
      const t = clamp01(state.oneShotTime / Math.max(clips.land.duration, 1e-3));
      const land = 1 - t * t;
      idle *= 1 - land;
      walk *= 1 - land;
      runW *= 1 - land;
      weights.land = land;
    }
    weights.idle = idle;
    weights.walk = walk;
    weights.run = runW;
  }

  // Lock cycle rate to ground speed: one stride per cycle.
  for (const clip of ["walk", "run"] as const) {
    const info = clips[clip];
    if (info.stride && info.stride > 0 && info.duration > 0) {
      const natural = info.stride / info.duration;
      const floor = clip === "walk" ? 0.5 : 0.7; // never freeze mid-blend
      speeds[clip] = Math.max(floor, motion.speed / natural);
    }
  }

  return { weights, speeds, restart };
}

/** Seconds to reach a new target weight (critically damped-ish ease). */
const BLEND_TIME = 0.1;

export class CatAnimator {
  private readonly groups: Record<CatClip, AnimationGroup>;
  private readonly info: Record<CatClip, CatClipInfo>;
  private readonly state = initialCatPoseState();
  private readonly weights: Record<CatClip, number> = { idle: 1, walk: 0, run: 0, jump: 0, fall: 0, land: 0 };

  /**
   * Returns null unless every clip is present — a cat without the full set
   * keeps the procedural pose instead of half-animating.
   */
  static create(
    groups: readonly AnimationGroup[],
    info?: Partial<Record<CatClip, CatClipInfo>>,
  ): CatAnimator | null {
    const byName = new Map(groups.map((g) => [g.name, g]));
    const found = {} as Record<CatClip, AnimationGroup>;
    for (const clip of CAT_CLIPS) {
      const g = byName.get(clip);
      if (!g) return null;
      found[clip] = g;
    }
    return new CatAnimator(found, info ?? {});
  }

  private constructor(groups: Record<CatClip, AnimationGroup>, info: Partial<Record<CatClip, CatClipInfo>>) {
    this.groups = groups;
    this.info = {} as Record<CatClip, CatClipInfo>;
    for (const clip of CAT_CLIPS) {
      const g = groups[clip];
      const loop = clip !== "jump" && clip !== "land";
      this.info[clip] = { duration: info[clip]?.duration ?? groupDuration(g), stride: info[clip]?.stride };
      g.stop();
      g.loopAnimation = loop;
      g.start(loop, 1);
      if (!loop) g.pause();
      g.weight = this.weights[clip];
    }
  }

  get clipInfo(): Readonly<Record<CatClip, CatClipInfo>> {
    return this.info;
  }

  update(motion: CatMotion, dt: number): void {
    const t = stepCatPose(this.state, motion, this.info, dt);
    if (t.restart) {
      const g = this.groups[t.restart];
      g.goToFrame(g.from);
      g.play(false);
      // Snap the one-shot in: anticipation frames are the point of it.
      this.weights[t.restart] = Math.max(this.weights[t.restart], 0.6);
    }
    const k = 1 - Math.exp(-dt / BLEND_TIME);
    for (const clip of CAT_CLIPS) {
      const w = this.weights[clip] + (t.weights[clip] - this.weights[clip]) * k;
      this.weights[clip] = w < 1e-3 ? 0 : w;
      const g = this.groups[clip];
      g.weight = this.weights[clip];
      g.speedRatio = t.speeds[clip];
    }
  }

  /** Current eased weight of a clip (for tests and debugging overlays). */
  weightOf(clip: CatClip): number {
    return this.weights[clip];
  }

  dispose(): void {
    for (const clip of CAT_CLIPS) this.groups[clip].stop();
  }
}

function groupDuration(g: AnimationGroup): number {
  const fps = g.targetedAnimations[0]?.animation.framePerSecond ?? 60;
  return Math.max(0, (g.to - g.from) / fps);
}
